/* eslint-disable max-len */

interface SvgType {
  className?: string;
  fill?: string;
}

export const SentinalIcon = ({ className, fill }: SvgType): JSX.Element => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 24" fill="none" className={className}>
    <g fill={fill || 'currentColor'}>
      <path d="M8.8 4.2L3.6 12l5.2 7.8c.3.5.2 1.1-.3 1.4-.5.3-1.1.2-1.4-.3L1.4 12l5.7-8.9c.3-.5.9-.6 1.4-.3.5.3.6.9.3 1.4z"/>
      <g transform="translate(7 0)">
        <path d="M15.688 2.343a2.588 2.588 0 00-3.61 0l-9.626 9.44a.863.863 0 01-1.203 0 .823.823 0 010-1.18l9.626-9.44a4.313 4.313 0 016.016 0 4.116 4.116 0 011.204 3.54 4.3 4.3 0 013.609 1.18l.05.05a4.115 4.115 0 010 5.9l-8.706 8.537a.274.274 0 000 .393l1.788 1.754a.823.823 0 010 1.18.863.863 0 01-1.203 0l-1.788-1.753a1.92 1.92 0 010-2.754l8.706-8.538a2.47 2.47 0 000-3.54l-.05-.049a2.588 2.588 0 00-3.607-.003l-7.172 7.034-.002.002-.098.097a.863.863 0 01-1.204 0 .823.823 0 010-1.18l7.273-7.133a2.47 2.47 0 00-.003-3.537z"/>
        <path d="M14.485 4.703a.823.823 0 000-1.18.863.863 0 00-1.204 0l-7.119 6.982a4.115 4.115 0 000 5.9 4.314 4.314 0 006.016 0l7.12-6.982a.823.823 0 000-1.18.863.863 0 00-1.204 0l-7.119 6.982a2.588 2.588 0 01-3.61 0 2.47 2.47 0 010-3.54l7.12-6.982z"/>
      </g>
      <g transform="translate(-24 0)">
        <path d="M55.2 4.2c-.3-.5-.2-1.1.3-1.4.5-.3 1.1-.2 1.4.3L62.6 12l-5.7 8.9c-.3.5-.9.6-1.4.3-.5-.3-.6-.9-.3-1.4l5.2-7.8-5.2-7.8z"/>
      </g>
    </g>
  </svg>
)

export const SentinalLogo = ({ className, iconColor, textColor, size = 'md' }: {
  className?: string;
  iconColor?: string;
  textColor?: string;
  size?: 'sm' | 'md' | 'lg';
}): JSX.Element => (
  <XxllncAiLogo className={className} textColor={textColor} size={size} />
)


// xxllnc.ai brand mark: rounded square with a diagonal teal → indigo/purple
// base plus a luminous steel-blue glow at the bottom-right corner, and a white
// "AI". Recreated as SVG (from public/xxlln_ai.png) so it stays crisp at every size.
const aiMark = (idPrefix: string): JSX.Element => (
  <>
    <defs>
      <linearGradient id={`${idPrefix}-base`} x1="2" y1="2" x2="46" y2="46" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#123A3A" />
        <stop offset="0.42" stopColor="#262061" />
        <stop offset="0.72" stopColor="#3A2E78" />
        <stop offset="1" stopColor="#4C4C95" />
      </linearGradient>
      <radialGradient id={`${idPrefix}-glow`} cx="0.82" cy="0.9" r="0.78">
        <stop offset="0" stopColor="#A6BEDA" stopOpacity="0.9" />
        <stop offset="0.45" stopColor="#6F8FB0" stopOpacity="0.35" />
        <stop offset="1" stopColor="#6F8FB0" stopOpacity="0" />
      </radialGradient>
    </defs>
    <rect width="48" height="48" rx="11" fill={`url(#${idPrefix}-base)`} />
    <rect width="48" height="48" rx="11" fill={`url(#${idPrefix}-glow)`} />
    <text
      x="24"
      y="25"
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily="Plus Jakarta Sans, Inter, sans-serif"
      fontSize="20"
      fontWeight="700"
      letterSpacing="-0.01em"
      fill="#FFFFFF"
    >
      AI
    </text>
  </>
)

export const XxllncAiMark = ({ className, size = 24 }: {
  className?: string;
  size?: number;
}): JSX.Element => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" fill="none" className={className} width={size} height={size}>
    {aiMark('xxAiMarkGrad')}
  </svg>
)

export const XxllncAiLogo = ({ className, textColor, size = 'md' }: {
  className?: string;
  textColor?: string;
  size?: 'sm' | 'md' | 'lg';
}): JSX.Element => {
  const height = size === 'lg' ? 40 : size === 'sm' ? 20 : 28
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 188 48" fill="none" height={height} className={className}>
      <svg x="0" y="0" width="48" height="48" viewBox="0 0 48 48">
        {aiMark('xxAiLogoGrad')}
      </svg>
      <text
        x="60"
        y="24"
        dominantBaseline="central"
        fontFamily="Plus Jakarta Sans, Inter, sans-serif"
        fontSize="26"
        fontWeight="700"
        letterSpacing="-0.02em"
        fill={textColor || '#253649'}
      >
        xxllnc.ai
      </text>
    </svg>
  )
}

/** @deprecated Use SentinalIcon instead */
export const Xxllnc = SentinalIcon

export const Dot = ({ className, fill = 'currentColor' }: SvgType): JSX.Element => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} height="5px" width="5px" viewBox="0 0 1 1">
    <circle fill={fill} cx="0.5" cy="0.5" r="0.5" />
  </svg>
)

export const moreAppsBase64 = 'PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHZpZXdCb3g9IjAgMCA1MCA1MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxnIGNsaXAtcGF0aD0idXJsKCNjbGlwMF8yXzM1KSI+DQo8cGF0aCBkPSJNMjIuNSAwSDBWMjIuNUgyMi41VjBaIiBmaWxsPSIjMzE2NzlCIi8+DQo8L2c+DQo8ZyBjbGlwLXBhdGg9InVybCgjY2xpcDFfMl8zNSkiPg0KPHBhdGggZD0iTTIyLjUgMjcuNUgwVjUwSDIyLjVWMjcuNVoiIGZpbGw9IiNFNTk5MjUiLz4NCjwvZz4NCjxnIGNsaXAtcGF0aD0idXJsKCNjbGlwMl8yXzM1KSI+DQo8cGF0aCBkPSJNNTAgMEgyNy41VjIyLjVINTBWMFoiIGZpbGw9IiMwMDZFNUIiLz4NCjwvZz4NCjxnIGNsaXAtcGF0aD0idXJsKCNjbGlwM18yXzM1KSI+DQo8cGF0aCBkPSJNNTAgMjcuNUgyNy41VjUwSDUwVjI3LjVaIiBmaWxsPSIjRTE1QjMyIi8+DQo8L2c+DQo8ZGVmcz4NCjxjbGlwUGF0aCBpZD0iY2xpcDBfMl8zNSI+DQo8cmVjdCB3aWR0aD0iMjIuNSIgaGVpZ2h0PSIyMi41IiByeD0iOCIgZmlsbD0id2hpdGUiLz4NCjwvY2xpcFBhdGg+DQo8Y2xpcFBhdGggaWQ9ImNsaXAxXzJfMzUiPg0KPHJlY3QgeT0iMjcuNSIgd2lkdGg9IjIyLjUiIGhlaWdodD0iMjIuNSIgcng9IjgiIGZpbGw9IndoaXRlIi8+DQo8L2NsaXBQYXRoPg0KPGNsaXBQYXRoIGlkPSJjbGlwMl8yXzM1Ij4NCjxyZWN0IHg9IjI3LjUiIHdpZHRoPSIyMi41IiBoZWlnaHQ9IjIyLjUiIHJ4PSI4IiBmaWxsPSJ3aGl0ZSIvPg0KPC9jbGlwUGF0aD4NCjxjbGlwUGF0aCBpZD0iY2xpcDNfMl8zNSI+DQo8cmVjdCB4PSIyNy41IiB5PSIyNy41IiB3aWR0aD0iMjIuNSIgaGVpZ2h0PSIyMi41IiByeD0iOCIgZmlsbD0id2hpdGUiLz4NCjwvY2xpcFBhdGg+DQo8L2RlZnM+DQo8L3N2Zz4NCg=='
